(function () {
  // Guard against redeclaring the class / redefining the custom element if this section
  // (and thus its own <script src> tag) is added to a page more than once.
  if (window.VideoCarouselLoaded) return;
  window.VideoCarouselLoaded = true;

  // No slider library here on purpose: the phone frame and the two preview
  // boxes are always the same fixed size and never move. Next/prev/autoplay
  // only ever (a) crossfade which .video-carousel__slide is active inside the
  // phone — the ios.png frame image itself is untouched, so it never blinks —
  // and (b) swap the prev/next preview <img> `src` in place. Nothing slides,
  // nothing resizes, so the section's height never shifts either.
  class VideoCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.video-carousel__slide'));
      if (this.slides.length === 0) return;

      this.screen = this.querySelector('.video-carousel__screen');

      const activeFromMarkup = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.activeIndex = activeFromMarkup === -1 ? 0 : activeFromMarkup;
      this.loop = this.dataset.loop !== 'false';

      this.prevButton = this.querySelector('.video-carousel__arrow--prev');
      this.nextButton = this.querySelector('.video-carousel__arrow--next');
      this.prevPreview = this.querySelector('.video-carousel__preview--prev');
      this.nextPreview = this.querySelector('.video-carousel__preview--next');

      this.onPrevClick = () => this.goTo(this.activeIndex - 1);
      this.onNextClick = () => this.goTo(this.activeIndex + 1);
      this.hovering = false;
      this.onMouseEnter = () => {
        this.hovering = true;
        this.stopAutoplay();
      };
      this.onMouseLeave = () => {
        this.hovering = false;
        this.startAutoplay();
      };

      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.prevPreview?.addEventListener('click', this.onPrevClick);
      this.nextPreview?.addEventListener('click', this.onNextClick);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);

      // As soon as the active slide's video finishes playing, move on to the
      // next slide (which becomes active and so appears centered in the
      // phone frame) instead of waiting for the fixed autoplay interval.
      this.videoEndedHandlers = new Map();
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;
        const onEnded = () => {
          if (index !== this.activeIndex || this.dataset.autoplay !== 'true') return;
          if (this.hovering || this.reducedMotion) return;
          this.goTo(this.activeIndex + 1);
        };
        video.addEventListener('ended', onEnded);
        this.videoEndedHandlers.set(video, onEnded);
      });

      // Videos are preload="none" and only play while the carousel is on (or near) screen. Playing
      // straight away started streaming the active video during page load from far down the page,
      // which competed with the hero for bandwidth and wrecked LCP/Speed Index on mobile.
      this.inView = false;
      if ('IntersectionObserver' in window) {
        this.viewObserver = new IntersectionObserver(
          (entries) => {
            this.inView = entries.some((entry) => entry.isIntersecting);
            // Resume/pause in place rather than restarting from 0 each time it scrolls back in.
            const video = this.slides[this.activeIndex]?.querySelector('video');
            if (!video) return;
            if (this.inView) {
              const playPromise = video.play();
              if (playPromise && playPromise.catch) playPromise.catch(() => {});
            } else {
              video.pause();
            }
          },
          { rootMargin: '200px 0px' }
        );
        this.viewObserver.observe(this);
      } else {
        this.inView = true;
        this.playActiveVideo();
      }

      this.updateArrows();
      this.updatePreviews();

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.dataset.autoplay === 'true' && !this.reducedMotion) this.startAutoplay();
    }

    disconnectedCallback() {
      this.viewObserver?.disconnect();
      this.stopAutoplay();
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.prevPreview?.removeEventListener('click', this.onPrevClick);
      this.nextPreview?.removeEventListener('click', this.onNextClick);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.videoEndedHandlers?.forEach((onEnded, video) => video.removeEventListener('ended', onEnded));
      this.leavingTimers?.forEach((timer) => clearTimeout(timer));
      this.leavingTimers?.clear();
      this.previewTimers?.forEach((timer) => clearTimeout(timer));
      this.previewTimers?.clear();
    }

    goTo(index) {
      const count = this.slides.length;
      if (count === 0 || index === this.activeIndex) return;

      let nextIndex = index;
      if (this.loop) {
        nextIndex = ((index % count) + count) % count;
      } else {
        nextIndex = Math.max(0, Math.min(count - 1, index));
        if (nextIndex === this.activeIndex) return;
      }

      // Which way the new slide travels across the screen. `index` is the raw
      // request (activeIndex +/- 1 from the arrows), so it still reads the
      // right direction on the wrap-around, where nextIndex does not.
      const forward = index > this.activeIndex;
      if (this.screen) this.screen.dataset.direction = forward ? 'next' : 'prev';

      const outgoing = this.slides[this.activeIndex];
      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-leaving');
      this.clearLeaving(outgoing);

      this.activeIndex = nextIndex;
      this.slides[this.activeIndex].classList.remove('is-leaving');
      this.slides[this.activeIndex].classList.add('is-active');

      this.playActiveVideo();
      this.updateArrows();
      this.updatePreviews();
      if (!this.hovering) this.startAutoplay();
    }

    // The leaving slide is held off-screen by the animation's `both` fill, so
    // drop the class once it has finished and let it go back to being an
    // ordinary hidden slide. Tracked per slide: rapid clicks can start a new
    // exit before the last one has ended.
    clearLeaving(slide) {
      this.leavingTimers = this.leavingTimers || new Map();
      clearTimeout(this.leavingTimers.get(slide));
      this.leavingTimers.set(
        slide,
        setTimeout(() => {
          slide.classList.remove('is-leaving');
          this.leavingTimers.delete(slide);
        }, 700)
      );
    }

    updatePreviews() {
      const count = this.slides.length;
      const hasNeighbors = count > 1;

      const setPreview = (button, slide) => {
        if (!button) return;
        if (!hasNeighbors || !slide) {
          button.hidden = true;
          return;
        }
        button.hidden = false;
        this.swapPreview(button, slide.dataset.thumb || '', slide.dataset.thumbSrcset || '');
      };

      const prevSlide = this.loop
        ? this.slides[(this.activeIndex - 1 + count) % count]
        : this.slides[this.activeIndex - 1];
      const nextSlide = this.loop
        ? this.slides[(this.activeIndex + 1) % count]
        : this.slides[this.activeIndex + 1];

      setPreview(this.prevPreview, prevSlide === this.slides[this.activeIndex] ? null : prevSlide);
      setPreview(this.nextPreview, nextSlide === this.slides[this.activeIndex] ? null : nextSlide);
    }

    // Slides a neighbour thumbnail across its card the same way the video moves
    // behind the phone: the incoming image is stacked on top of the outgoing
    // one and both animate, rather than swapping `src` on a single <img>.
    // Before the first change there is no direction to travel in, so the very
    // first paint just sets the source outright.
    swapPreview(button, src, srcset) {
      const current = button.querySelector('.video-carousel__preview-img:not(.is-leaving)');
      if (!current || current.getAttribute('src') === src) return;

      const direction = this.screen && this.screen.dataset.direction;
      if (!direction || this.reducedMotion) {
        current.srcset = srcset;
        current.src = src;
        return;
      }

      button.dataset.direction = direction;

      const incoming = current.cloneNode(false);
      incoming.className = 'video-carousel__preview-img is-entering';
      incoming.srcset = srcset;
      incoming.src = src;
      // A click landing mid-animation hands over cleanly: whatever was coming
      // in becomes the one going out.
      current.classList.remove('is-entering');
      current.classList.add('is-leaving');
      button.appendChild(incoming);

      this.previewTimers = this.previewTimers || new Map();
      clearTimeout(this.previewTimers.get(button));
      this.previewTimers.set(
        button,
        setTimeout(() => {
          // Anything still on its way out is now off the card — drop it all and
          // leave the newest image as the card's only child.
          button.querySelectorAll('.video-carousel__preview-img.is-leaving').forEach((img) => img.remove());
          incoming.classList.remove('is-entering');
          this.previewTimers.delete(button);
        }, 700)
      );
    }

    playActiveVideo() {
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (index === this.activeIndex && this.inView) {
          video.currentTime = 0;
          const playPromise = video.play();
          if (playPromise && playPromise.catch) playPromise.catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    updateArrows() {
      if (this.loop) return;
      if (this.prevButton) this.prevButton.disabled = this.activeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex === this.slides.length - 1;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.dataset.autoplay !== 'true' || this.slides.length < 2) return;
      // A slide with a video advances on its own via the 'ended' listener
      // above, so the fixed-delay timer only needs to drive slides that
      // have no video (image-only fallback slides).
      const activeSlide = this.slides[this.activeIndex];
      if (activeSlide.querySelector('video')) return;
      const delay = parseInt(this.dataset.autoplaySpeed, 10) || 6000;
      this.autoplayTimer = setInterval(() => this.goTo(this.activeIndex + 1), delay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }
  }

  if (!customElements.get('video-carousel')) {
    customElements.define('video-carousel', VideoCarousel);
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const el = event.target.querySelector('video-carousel');
      if (!el) return;
      el.disconnectedCallback();
      el.connectedCallback();
    });

    document.addEventListener('shopify:section:reorder', () => {
      document.querySelectorAll('video-carousel').forEach((el) => {
        el.disconnectedCallback();
        el.connectedCallback();
      });
    });

    document.addEventListener('shopify:block:select', (event) => {
      const carousel = event.target.closest('video-carousel');
      const slide = event.target.closest('.video-carousel__slide');
      if (!carousel || !slide || !carousel.slides) return;
      carousel.stopAutoplay();
      carousel.goTo(carousel.slides.indexOf(slide));
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const carousel = event.target.closest('video-carousel');
      if (!carousel) return;
      carousel.startAutoplay();
    });
  }
})();
